import { useMemo } from 'react'
import { Boxes } from 'lucide-react'
import type { AdminColumn } from '@/components/DataTable/types'
import { SecCrudTable, type SecField } from './SecCrudTable'
import { SEC_STR as S, type DepAuditRow } from './securityMoreApi'

/**
 * A18.21 — Bog'liqliklar auditi. Admin-managed dependency-issue registry
 * (`sec_dep_audit`, 000248): one row per vulnerable package/version with its
 * CVE, severity, fix and triage status. Real CRUD via `/admin/security/dep-audit`.
 */
const SEVERITY_OPTS = [
  { value: 'critical', label: 'critical' },
  { value: 'high', label: 'high' },
  { value: 'medium', label: 'medium' },
  { value: 'low', label: 'low' },
]
const STATUS_OPTS = [
  { value: 'open', label: 'open' },
  { value: 'patched', label: 'patched' },
  { value: 'ignored', label: 'ignored' },
]

export function DependencyAuditScreen() {
  const columns = useMemo<AdminColumn<DepAuditRow>[]>(
    () => [
      { key: 'package', header: S.pkg, type: 'text' },
      { key: 'version', header: S.version, type: 'text', width: 120 },
      { key: 'cve', header: S.cve, type: 'text', width: 150 },
      {
        key: 'severity',
        header: S.severity,
        type: 'enum',
        width: 120,
        options: [
          { value: 'critical', label: S.critical, status: 'banned' },
          { value: 'high', label: S.high, status: 'blocked' },
          { value: 'medium', label: S.medium, status: 'review' },
          { value: 'low', label: S.low, status: 'pending' },
        ],
      },
      { key: 'fixed_in', header: S.fixedIn, type: 'text', width: 130 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 130,
        options: [
          { value: 'open', label: S.openLbl, status: 'open' },
          { value: 'patched', label: 'patched', status: 'active' },
          { value: 'ignored', label: 'ignored', status: 'pending' },
        ],
      },
    ],
    [],
  )

  const fields = useMemo<SecField[]>(
    () => [
      { name: 'package', placeholder: S.pkg, mono: true },
      { name: 'version', placeholder: S.version, mono: true },
      { name: 'cve', placeholder: S.cve, mono: true },
      { name: 'severity', placeholder: S.severity, type: 'select', options: SEVERITY_OPTS },
      { name: 'fixed_in', placeholder: S.fixedIn, mono: true },
      { name: 'status', placeholder: S.status, type: 'select', options: STATUS_OPTS },
    ],
    [],
  )

  return (
    <SecCrudTable<DepAuditRow>
      screenId="A18.21"
      title={S.depAudit}
      addLabel={S.addDep}
      path="/admin/security/dep-audit"
      listKey="findings"
      searchKeys={['package', 'cve', 'version']}
      fields={fields}
      columns={columns}
      icon={<Boxes size={28} strokeWidth={1.5} />}
    />
  )
}
